<template>
  <div class="ai-helper-container">
    <div class="ai-helper-header">
      <div class="ai-helper-title">
        <i class="fas fa-robot"></i>
        <h3>BookedSolid Help Assistant</h3>
        <span class="beta-tag">Beta</span>
      </div>
    </div>

    <div class="ai-helper-content">
      <div class="messages-container" ref="messagesContainer">
        <div 
          v-for="(message, index) in messages" 
          :key="index" 
          class="message"
          :class="{ 'user-message': message.role === 'user', 'ai-message': message.role === 'assistant' }"
        >
          <div class="message-avatar">
            <i v-if="message.role === 'user'" class="fas fa-user"></i>
            <i v-else class="fas fa-robot"></i>
          </div>
          <div class="message-content">
            <div 
              v-if="message.role === 'assistant'" 
              class="message-text" 
              v-html="formatAIContent(message.content)"
            ></div>
            <div v-else class="message-text" v-html="formatUserContent(message.content)"></div>
            <div class="message-time">{{ formatTime(message.timestamp) }}</div>
          </div>
        </div>
        
        <div v-if="isLoading" class="message ai-message">
          <div class="message-avatar">
            <i class="fas fa-robot"></i>
          </div>
          <div class="message-content">
            <div class="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>

      <div class="input-container">
        <div class="input-wrapper">
          <textarea
            id="ai-helper-input"
            v-model="currentMessage"
            @keydown="handleKeydown"
            @input="autoResize"
            ref="messageInput"
            placeholder="Ask me about your BookedSolid settings, integrations, or configuration..."
            :disabled="isLoading"
            rows="1"
          ></textarea>
          <button 
            @click="sendMessage" 
            :disabled="!currentMessage.trim() || isLoading"
            class="send-btn"
          >
            <i class="fas fa-paper-plane"></i>
          </button>
        </div>
        <div class="input-hint">
          Press Enter to send, Shift+Enter for new line
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { escapeHtml } from '@/utils/xss-protection.js';
import { processAIHelperRequest } from '@/app/admin-routes';

export default {
  name: 'AIHelper',
  data() {
    return {
      messages: [],
      currentMessage: '',
      isLoading: false,
      storageKey: 'ai-helper-messages',
      chatId: null,
    };
  },
  methods: {
    addWelcomeMessage() {
      const welcomeMessage = {
        role: 'assistant',
        content: `Hello! I'm your BookedSolid AI assistant. I can help you with questions about your settings, integrations, and configuration.
        
        What would you like to know about your BookedSolid setup?`,
        timestamp: new Date()
      };
      this.messages.push(welcomeMessage);
      this.saveMessages();
    },
    
    async sendMessage() {
      if (!this.currentMessage.trim() || this.isLoading) return;
      
      const userMessage = {
        role: 'user',
        content: this.currentMessage.trim(),
        timestamp: new Date()
      };
      
      this.messages.push(userMessage);
      this.currentMessage = '';
      this.isLoading = true;
      this.saveMessages();
      
      this.$nextTick(() => {
        this.scrollToBottom();
        this.autoResize();
      });

      // Generate a chat ID on the first user message and persist it for the session
      if (!this.chatId) {
        this.chatId = `hc_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
        this.saveChatId();
      }
      
      try {
        const response = await processAIHelperRequest(
          this.messages.map(msg => ({ role: msg.role, content: msg.content })),
          this.chatId
        );
        
        if (response.success && response.message) {
          const aiMessage = {
            role: 'assistant',
            content: response.message,
            timestamp: new Date()
          };
          this.messages.push(aiMessage);
        } else {
          throw new Error(response.message || 'Failed to get AI response');
        }
      } catch (error) {
        console.error('AI Helper Error:', error);
        const errorMessage = {
          role: 'assistant',
          content: `I'm sorry, I encountered an error: ${error.message}. Please try again or contact support if the issue persists.`,
          timestamp: new Date()
        };
        this.messages.push(errorMessage);
      } finally {
        this.isLoading = false;
        this.saveMessages();
        this.$nextTick(() => {
          this.scrollToBottom();
          this.$refs.messageInput?.focus();
        });
      }
    },
    
    handleKeydown(event) {
      if (event.key === 'Enter') {
        if (event.shiftKey) {
          // Allow shift+enter for new line - don't prevent default
          return;
        } else {
          // Prevent default and send message
          event.preventDefault();
          this.sendMessage();
        }
      }
    },
    
    autoResize() {
      const textarea = this.$refs.messageInput;
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
      }
    },
    
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          // Use smooth scrolling and ensure we reach the bottom
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth'
          });
        }
      });
    },
    
    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    },
    
    formatAIContent(content) {
      // Convert markdown-style formatting to HTML
      let formatted = escapeHtml(content)
        // Convert **bold** to <strong>bold</strong>
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        // Convert *italic* to <em>italic</em>
        .replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '<em>$1</em>')
        // Convert newlines to HTML line breaks
        .replace(/\n/g, '<br>');
      
      return formatted;
    },
    
    formatUserContent(content) {
      // Escape HTML and convert newlines to line breaks for user content
      const escaped = content
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
      return escaped.replace(/\n/g, '<br>');
    },
    
    saveMessages() {
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.messages));
    },

    saveChatId() {
      sessionStorage.setItem(`${this.storageKey}-chat-id`, this.chatId || '');
    },
    
    loadMessages() {
      try {
        const saved = sessionStorage.getItem(this.storageKey);
        if (saved) {
          this.messages = JSON.parse(saved).map(msg => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }));
        }
        const savedChatId = sessionStorage.getItem(`${this.storageKey}-chat-id`);
        if (savedChatId) this.chatId = savedChatId;
        
        // If no messages exist, add welcome message
        if (this.messages.length === 0) {
          this.addWelcomeMessage();
        }
      } catch (error) {
        console.error('Error loading AI helper messages:', error);
        this.messages = [];
        this.addWelcomeMessage();
      }
    },
    
    clearChat() {
      this.messages = [];
      this.chatId = null;
      this.saveChatId();
      this.saveMessages();
      this.addWelcomeMessage();
    }
  },
  
  mounted() {
    this.loadMessages();
    // Scroll to bottom after messages are loaded and component is rendered
    this.$nextTick(() => {
      setTimeout(() => {
        this.scrollToBottom();
      }, 100); // Small delay to ensure DOM is fully rendered
    });
  }
};
</script>

<style scoped lang="scss">
@import "/src/assets/styles/variables.scss";

.ai-helper-container {
  width: 100%;
  max-width: 1200px;
  margin: auto;
  box-sizing: border-box;
  background: linear-gradient(135deg, #21c0eb 0%, #2562be 70%);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  text-align: left;
  color: white;
  position: relative;
}

.ai-helper-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px 16px;
  cursor: pointer;
  
  .ai-helper-title {
    display: flex;
    align-items: center;
    gap: 12px;
    
    i {
      font-size: 1.5em;
      color: #fff;
    }
    
    h3 {
      margin: 0;
      font-size: 1.4em;
      font-weight: 600;
      color: #fff;
    }
    
    .beta-tag {
      background: rgba(255, 255, 255, 0.25);
      color: #fff;
      font-size: 0.7em;
      font-weight: 600;
      padding: 3px 8px;
      border-radius: 12px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-left: 4px;
      border: 1px solid rgba(255, 255, 255, 0.3);
    }
  }
  
  .toggle-btn {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    
    i {
      color: white;
      transition: transform 0.3s ease;
    }
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
    
    &.expanded i {
      transform: rotate(180deg);
    }
  }
}

.ai-helper-subtitle {
  padding: 0 24px 20px;
  
  p {
    margin: 0;
    font-size: 1em;
    opacity: 0.9;
    line-height: 1.4;
  }
}

.ai-helper-content {
  background: white;
  color: #333;
  border-radius: 0 0 12px 12px;
  display: flex;
  flex-direction: column;
  height: 500px;
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
    
    &:hover {
      background: #a8a8a8;
    }
  }
}

.message {
  display: flex;
  gap: 12px;
  max-width: 85%;
  
  &.user-message {
    align-self: flex-end;
    flex-direction: row-reverse;
    
    .message-content {
      background: $colourS;
      color: white;
    }
    
    .message-avatar {
      background: $colourS;
    }
  }
  
  &.ai-message {
    align-self: flex-start;
    
    .message-content {
      background: #f8f9fa;
      color: #333;
    }
    
    .message-avatar {
      background: #21c0eb;
    }
  }
}

.message-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  i {
    color: white;
    font-size: 0.9em;
  }
}

.message-content {
  border-radius: 16px;
  padding: 12px 16px;
  position: relative;
  
  .message-text {
    line-height: 1.5;
    word-wrap: break-word;
    
    // Style HTML content
    :deep(ul), :deep(ol) {
      margin: 8px 0;
      padding-left: 20px;
    }
    
    :deep(li) {
      margin: 4px 0;
    }
    
    :deep(a) {
      color: $colourS;
      text-decoration: none;
      font-weight: 500;
      
      &:hover {
        text-decoration: underline;
      }
    }
    
    :deep(strong), :deep(b) {
      font-weight: 600;
    }
    
    :deep(p) {
      margin: 8px 0;
      
      &:first-child {
        margin-top: 0;
      }
      
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
  
  .message-time {
    font-size: 0.75em;
    opacity: 0.7;
    margin-top: 4px;
  }
}

.typing-indicator {
  display: flex;
  gap: 4px;
  align-items: center;
  
  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #999;
    animation: typing 1.4s infinite;
    
    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  30% {
    transform: translateY(-10px);
    opacity: 1;
  }
}

.input-container {
  border-top: 1px solid #e5e7eb;
  padding: 16px 20px;
  background: #fafbfc;
  border-radius: 0 0 12px 12px;
}

.input-wrapper {
  display: flex;
  gap: 8px;
  align-items: flex-end;
  
  textarea {
    flex: 1;
    border: 1px solid #d1d5db;
    border-radius: 20px;
    padding: 12px 16px;
    font-family: inherit;
    font-size: 0.95em;
    resize: none;
    min-height: 44px;
    max-height: 120px;
    overflow-y: auto;
    transition: border-color 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: $colourS;
    }
    
    &:disabled {
      background: #f3f4f6;
      cursor: not-allowed;
    }
    
    &::placeholder {
      color: #9ca3af;
    }
  }
  
  .send-btn {
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 50%;
    background: $colourS;
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;
    
    &:hover:not(:disabled) {
      background: darken($colourS, 10%);
      transform: scale(1.05);
    }
    
    &:disabled {
      background: #d1d5db;
      cursor: not-allowed;
      transform: none;
    }
    
    i {
      font-size: 0.9em;
    }
  }
}

.input-hint {
  font-size: 0.8em;
  color: #6b7280;
  margin-top: 8px;
  text-align: center;
}

@media (max-width: 768px) {
  .ai-helper-container {
    margin: 16px 10px;
  }
  
  .ai-helper-header {
    padding: 16px 20px 12px;
    
    .ai-helper-title {
      h3 {
        font-size: 1.2em;
      }
      
      i {
        font-size: 1.3em;
      }
    }
  }
  
  .ai-helper-content {
    height: 400px;
  }
  
  .messages-container {
    padding: 16px;
  }
  
  .message {
    max-width: 95%;
  }
  
  .input-container {
    padding: 12px 16px;
  }
  
  .input-wrapper textarea {
    font-size: 16px; // Prevent zoom on iOS
  }
}

@media (max-width: 480px) {
  .ai-helper-content {
    height: 350px;
  }
  
  .message-content {
    padding: 10px 14px;
  }
}
</style>
